import React from 'react';

import { inToPx } from '@aline/common';
import { equals } from 'ramda';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars/helpers';
import { Ticks } from 'src/editor/toolbars/rulers/Ticks';
import { useMarginAdjuster } from 'src/editor/toolbars/rulers/utils';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useCurrentUserId } from 'src/gql';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

export const verticalRulerWidthPx = 16;

const useStyles = makeStyles()(theme => ({
  line: {
    backgroundColor: theme.appColors.primary,
    height: 1,
    left: 17,
    position: 'absolute',
    top: 16,
    width: 'calc(100% - 17px)',
  },
  pageArea: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  root: {
    backgroundColor: theme.appColors.rulerMargin,
    borderRight: `1px ${theme.appColors.rule} solid`,
    height: '100%',
    left: 0,
    paddingTop: 16,
    position: 'absolute',
    top: 0,
    width: verticalRulerWidthPx,
  },
}));

interface VerticalRulerProps {
  active: boolean;
}

export function VerticalRuler({ active }: VerticalRulerProps): JSX.Element | null {
  const { classes } = useStyles();
  const { commenting, viewing } = useEditingMode();
  const { walkThroughActive } = useWalkthroughActive();
  const { dragTopMargin, topMarginDelta, topMarginDragPosition } = useDragTopMargin();
  const { bottomMarginDelta, bottomMarginDragPosition, dragBottomMargin } = useDragBottomMargin();

  const { showRulers } = useEditorViewPreferences();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );

  const pageHeight = pageDimensions.height;
  const contentHeight = pageHeight - topMarginDragPosition - bottomMarginDragPosition;

  if (walkThroughActive || !showRulers || viewing || commenting) return null;

  return (
    <>
      <div className={classes.root}>
        {active && (
          <div className={classes.pageArea}>
            <Ticks
              contentSize={contentHeight}
              direction="vertical"
              inchTickCount={Math.ceil(pageHeight)}
              marginStart={topMarginDragPosition}
              onEndDrag={dragBottomMargin}
              onStartDrag={dragTopMargin}
            />
          </div>
        )}
      </div>
      {topMarginDelta !== null && (
        <div
          className={classes.line}
          style={{
            transform: `translateY(${inToPx(topMarginDragPosition)}px)`,
          }}
        />
      )}
      {bottomMarginDelta !== null && (
        <div
          className={classes.line}
          style={{
            transform: `translateY(${inToPx(topMarginDragPosition + contentHeight)}px)`,
          }}
        />
      )}
    </>
  );
}

export function useDragTopMargin() {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const { delta: topMarginDelta, start: dragTopMargin } = useMarginAdjuster(
    editor,
    editingMode,
    currentUserId,
    'vertical',
    'start'
  );
  const safeDelta = topMarginDelta ?? 0;

  return {
    dragTopMargin,
    topMarginDelta,
    topMarginDragPosition: pageDimensions.marginTop + safeDelta,
  };
}

export function useDragBottomMargin() {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const { delta: bottomMarginDelta, start: dragBottomMargin } = useMarginAdjuster(
    editor,
    editingMode,
    currentUserId,
    'vertical',
    'end'
  );
  const safeDelta = bottomMarginDelta ?? 0;

  return {
    bottomMarginDelta,
    bottomMarginDragPosition: pageDimensions.marginBottom + safeDelta,
    dragBottomMargin,
  };
}
